import { CollapsedSidebar, Dashboard, ExpandedSidebar, WebsiteClick } from '@onesource/icons';
import type { IconType } from 'react-icons';
import { FaDumbbell, FaGraduationCap, FaHeart, FaStar, FaUsers } from 'react-icons/fa';
import { GiLotus, GiMeditation, GiYinYang } from 'react-icons/gi';
import {
  RiApps2Line,
  RiBookOpenLine,
  RiChat1Line,
  RiCompassLine,
  RiDashboardLine,
  RiFacebookLine,
  RiFileList3Line,
  RiGroupLine,
  RiHome4Line,
  RiInstagramLine,
  RiLinkedinLine,
  RiLinksLine,
  RiStarLine,
  RiTiktokLine,
  RiYoutubeLine,
} from 'react-icons/ri';

import type { SidebarItem, SidebarSection, SocialLink } from './types';

// Map icon string names to react-icons
const iconMap: Record<string, IconType> = {
  // Generic icons
  default: GiMeditation,
  home: RiHome4Line,
  book: RiBookOpenLine,
  chat: RiChat1Line,
  list: RiFileList3Line,
  apps: RiApps2Line,
  compass: RiCompassLine,
  dashboard: RiDashboardLine,
  star: RiStarLine,
  group: RiGroupLine,
  links: RiLinksLine,
  // Fitness/wellness icons
  meditation: GiMeditation,
  lotus: GiLotus,
  yinyang: GiYinYang,
  dumbbell: FaDumbbell,
  'graduation-cap': FaGraduationCap,
  users: FaUsers,
  heart: FaHeart,
  'star-filled': FaStar,
};

/**
 * Get an icon component by name, with fallback to default
 */
export const getIconForName = (iconName: string | null | undefined): IconType => {
  if (!iconName) {
    return iconMap.default;
  }
  return iconMap[iconName.toLowerCase()] || iconMap.default;
};

interface TopSectionOptions {
  isSubmenuCollapsed: boolean;
  onToggleSubmenu: () => void;
}

// Static top section — the submenu panel toggle, rendered as an icon-only button
export const getTopSectionItems = ({ isSubmenuCollapsed, onToggleSubmenu }: TopSectionOptions): SidebarItem[] => [
  {
    id: 'sidebar-toggle',
    label: isSubmenuCollapsed ? 'Expand sidebar' : 'Collapse sidebar',
    icon: isSubmenuCollapsed ? ExpandedSidebar : CollapsedSidebar,
    onClick: onToggleSubmenu,
    iconOnly: true,
  },
];

// Static main section items — Dashboard sits with Programs / Community
export const mainSectionItems: SidebarItem[] = [
  {
    id: 'welcome',
    label: 'Dashboard',
    icon: Dashboard,
    path: '/',
  },
];

// Static bottom section items
export const getBottomSectionItems = (onOldPanelClick: () => void): SidebarItem[] => [
  {
    id: 'classic-area',
    label: 'Classic Area',
    icon: WebsiteClick,
    onClick: onOldPanelClick,
  },
];

// Default sidebar data (used when profile is not loaded yet)
export const sidebarData: SidebarSection[] = [
  {
    id: 'top',
    items: getTopSectionItems({ isSubmenuCollapsed: false, onToggleSubmenu: () => undefined }),
    showDivider: true,
    desktopOnly: true,
  },
  {
    id: 'main',
    items: mainSectionItems,
    showDivider: true,
  },
  {
    id: 'bottom',
    items: getBottomSectionItems(() => undefined),
  },
];

export const socialLinks: SocialLink[] = [
  {
    id: 'instagram',
    icon: RiInstagramLine,
    url: 'https://instagram.com/',
    label: 'Instagram',
  },
  {
    id: 'tiktok',
    icon: RiTiktokLine,
    url: 'https://tiktok.com/',
    label: 'TikTok',
  },
  {
    id: 'facebook',
    icon: RiFacebookLine,
    url: 'https://facebook.com/',
    label: 'Facebook',
  },
  {
    id: 'youtube',
    icon: RiYoutubeLine,
    url: 'https://youtube.com/',
    label: 'YouTube',
  },
  {
    id: 'linkedin',
    icon: RiLinkedinLine,
    url: 'https://linkedin.com/',
    label: 'LinkedIn',
  },
];
